import type { ConstValues } from '@openx/types/helpers.ts';
import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import { DomainTargeting } from '@openx/types/targeting/domains.ts';
import {
  AppInventoryType,
  CustomBlockingRulesOptions,
  CustomBlockingRulesSubdomain,
  InventoryContentOption,
  type PublisherInventoryRuleOptions,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { Dispatch, SetStateAction } from 'react';
export const optionsWithBulkAdd: InventoryContentOption[] = [
  InventoryContentOption.ADUNIT,
  InventoryContentOption.APP_BUNDLE_ID,
  InventoryContentOption.APP_GROUP,
  InventoryContentOption.PAGE_URL,
  InventoryContentOption.PUBLISHER_ID,
  InventoryContentOption.INSTANCE_HASH,
  InventoryContentOption.SITE,
  InventoryContentOption.KEYWORDS,
  InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID,
];

export const comparisonOptions = {
  [InventoryContentOption.ADUNIT]: [ComparisonType.EQUALS, ComparisonType.DOES_NOT_EQUAL],
  [InventoryContentOption.APP_BUNDLE_ID]: [ComparisonType.EQUALS, ComparisonType.DOES_NOT_EQUAL],
  [InventoryContentOption.PAGE_URL]: [
    ComparisonType.EQUALS,
    ComparisonType.DOES_NOT_EQUAL,
    ComparisonType.CONTAINS,
    ComparisonType.DOES_NOT_CONTAIN,
    ComparisonType.BEGINS_WITH,
    ComparisonType.DOES_NOT_BEGIN_WITH,
    ComparisonType.ENDS_WITH,
    ComparisonType.DOES_NOT_END_WITH,
  ],
  [InventoryContentOption.SITE]: [ComparisonType.EQUALS, ComparisonType.DOES_NOT_EQUAL],
  [InventoryContentOption.APP_GROUP]: [ComparisonType.EQUALS, ComparisonType.DOES_NOT_EQUAL],
};

export const InputType = {
  Manually: 'manually',
  InventoryList: 'inventoryList',
} as const;

export type InputTypeValue = ConstValues<typeof InputType>;

export const inputOptions = Object.values(InputType);

export const publisherInventoryRuleOptionsInitialState: PublisherInventoryRuleOptions = {
  blockingRuleOption: CustomBlockingRulesOptions.DOMAIN,
  domainTargetingOption: DomainTargeting.ALL_VARIATIONS,
  isValid: false,
  publisherID: '',
};

export const customBlockingRulesSubtitle = {
  [CustomBlockingRulesOptions.BUNDLE_ID]: 'Paired App Bundle IDs:',
  [CustomBlockingRulesOptions.DOMAIN]: 'Paired Domain (Root Domains with Paths):',
  [CustomBlockingRulesSubdomain]: 'Paired Domain (All Subdomains with Paths):',
};

/**
 * App Inventory type options for the dropdown selector.
 * Determines whether user searches by App Name or App Bundle ID.
 */
export const appInventoryTypeOptions = [
  AppInventoryType.APP_NAME,
  AppInventoryType.APP_BUNDLE_ID,
  AppInventoryType.INVENTORY_LIST,
] as const;

// Type aliases for commonly used dispatch patterns
export type SetComparisonTypeDispatch = Dispatch<SetStateAction<ComparisonType>>;
export type SetAppInventoryTypeDispatch = Dispatch<SetStateAction<AppInventoryType>>;
export type SetInventoryListItemsDispatch = Dispatch<SetStateAction<InventoryListByIdAPI['values'] | undefined>>;

export type AppInventoryComparisonTypeProps = {
  appNameComparisonType?: ComparisonType;
  setAppNameComparisonType?: SetComparisonTypeDispatch;
  bundleIdComparisonType?: ComparisonType;
  setBundleIdComparisonType?: SetComparisonTypeDispatch;
};

/**
 * Common props for App Inventory components.
 * Used across InventoryAndContentTopBox, TopBoxRenderer, useGetDrawerContent, etc.
 */
export type AppInventoryProps = {
  appInventoryType?: AppInventoryType;
  setAppInventoryType?: SetAppInventoryTypeDispatch;
} & AppInventoryComparisonTypeProps;
